import React, { useState, useEffect, useRef, useContext } from "react";
import API from "../../../services/interceptor";
import { AuthContext } from "../../../context/AuthContext";

/**
 * AddCategoryItemModal
 * Props:
 *  - isOpen: boolean
 *  - selectedCategory: string (category name shown in the heading and used in the API call)
 *  - onClose: () => void
 *  - onAdded: (newItem) => void // optional callback called when item is successfully added
 *
 * Usage:
 * 
 * const [isOpen, setIsOpen] = useState(false);
 * const [selectedCategory, setSelectedCategory] = useState("allergiesName");
 * 
 * 
 * <AddCategoryItemModal
 *    isOpen={isOpen}
 *    selectedCategory={selectedCategory}
 *    onClose={() => setIsOpen(false)}
 *    onAdded={(item) => refreshDropdowns()}
 * />
 * <button 
      onClick={()=>setIsOpen(!isOpen)}
      >test
    </button>

 */

export default function AddCategoryItemModal({ isOpen, selectedCategory, onClose, onAdded }) {
    const { getDropDownMenu } = useContext(AuthContext);
  
    const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setValue("");
      setError(null);
      // focus input when modal opens
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen, selectedCategory]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return setError("Please enter an item.");

    try {
      setLoading(true);
      setError(null);

      // Use API.put as provided by the user
      // Make sure an `API` instance is available in scope (axios/fetch wrapper).
      const res = await API.put(`/api/dropdown/${encodeURIComponent(selectedCategory)}`, { item: trimmed });

      // Success handling
      if (onAdded) {
        try { onAdded(res?.data); } catch (_) {}
      }

      // close modal and reset
      setValue("");
      getDropDownMenu(); // Refresh dropdowns
      onClose?.();
    } catch (err) {
      console.error(err);
      // Basic error extraction
      const message = err?.response?.data?.message || err?.message || "Failed to add item.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={() => !loading && onClose?.()} />

      <div className="relative w-[min(95%,460px)] bg-white rounded-2xl shadow-xl ring-1 ring-black/5 p-6 mx-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold">{selectedCategory || "Category"}</h3>
            <p className="text-sm text-gray-500">Add a new item to this category</p>
          </div>
          <button
            onClick={() => !loading && onClose?.()}
            aria-label="Close"
            className="rounded-md p-2 hover:bg-gray-100"
            disabled={loading}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4">
          <label className="block text-sm font-medium mb-2">Item</label>
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder={`Enter new ${selectedCategory || 'item'}`}
            className="w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-300"
            disabled={loading}
          />

          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

          <div className="mt-4 flex items-center gap-3 justify-end">
            <button
              type="button"
              onClick={() => onClose?.()}
              disabled={loading}
              className="px-4 py-2 rounded-lg border hover:bg-gray-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading || !value.trim()}
              className={`px-4 py-2 rounded-lg font-semibold ${loading ? 'opacity-60 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
            >
              {loading ? 'Adding...' : 'Add item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
